class CartItemBundle {
  constructor() {
    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Add event listeners for add-on buttons
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('cart-addon-add-btn')) {
        this.handleAddOnClick(e);
      }
    });

    // Listen for cart updates to refresh addon availability
    document.addEventListener('cart:updated', () => {
      this.updateAddonAvailability();
    });
  }

  async handleAddOnClick(event) {
    const button = event.target;
    const variantId = button.dataset.variantId;
    const bundleId = button.dataset.bundleId;
    const mainQuantity = parseInt(button.dataset.mainQuantity) || 1;
    const addonTitle = button.dataset.addonTitle;

    if (!variantId || !bundleId) {
      console.error('Missing required data for add-on');
      return;
    }

    // Add loading state
    button.disabled = true;
    button.textContent = 'Adding...';
    const listItem = button.closest('.cart-addon-list-item');
    listItem.classList.add('cart-addon-loading');

    try {
      // Add the add-on to cart
      const config = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            id: parseInt(variantId, 10),
            quantity: mainQuantity,
            properties: {
              _bundle: bundleId,
              _is_addon: "true",
              addon_title: addonTitle
            }
          }]
        })
      };

      const response = await fetch('/cart/add.js', config);
      const data = await response.json();

      if (response.ok) {
        // Success - update UI
        this.markAddonAsAdded(listItem, addonTitle);
        
        // Trigger cart update
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { items: data.items }
        }));

        // Refresh the cart if there's a cart component
        const cartComponent = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartComponent && cartComponent.renderContents) {
          cartComponent.renderContents(data);
        } else {
          // Fallback: reload the page
          window.location.reload();
        }
      } else {
        throw new Error(data.message || 'Failed to add add-on to cart');
      }
    } catch (error) {
      console.error('Error adding add-on:', error);
      this.showError(button, 'Failed to add add-on');
    } finally {
      // Remove loading state
      listItem.classList.remove('cart-addon-loading');
    }
  }

  markAddonAsAdded(listItem, addonTitle) {
    listItem.classList.add('cart-addon-list-item--added');
    const button = listItem.querySelector('.cart-addon-add-btn');
    if (button) {
      // Replace add button with remove button
      const removeButton = document.createElement('a');
      removeButton.className = 'cart-addon-remove-btn';
      removeButton.textContent = 'Remove';
      removeButton.href = '#'; // This will be updated when cart refreshes
      removeButton.setAttribute('aria-label', `Remove ${addonTitle}`);
      
      button.parentNode.replaceChild(removeButton, button);
    }
  }

  showError(button, message) {
    button.textContent = 'Error';
    button.style.backgroundColor = '#DC2626';
    
    setTimeout(() => {
      button.textContent = 'Add';
      button.style.backgroundColor = '';
      button.disabled = false;
    }, 2000);
  }

  updateAddonAvailability() {
    // This would be called after cart updates to refresh which add-ons are available
    const addonCards = document.querySelectorAll('.cart-addon-card');
    addonCards.forEach(card => {
      const productId = card.dataset.addonProductId;
      // Check if this addon is already in the cart for any bundle
      // This is complex and might require a cart fetch, so for now we'll rely on page reload
    });
  }

  // Helper method to sync addon quantities with main product
  syncAddonQuantities(bundleId, newQuantity) {
    const addonRows = document.querySelectorAll(`tr[data-bundle-id="${bundleId}"].cart-item--addon`);
    addonRows.forEach(row => {
      const quantityDisplay = row.querySelector('.cart-addon-quantity__value');
      if (quantityDisplay) {
        quantityDisplay.textContent = newQuantity;
      }
    });
  }
}

// Enhanced cart quantity management for bundles
class CartBundleQuantityManager {
  constructor() {
    this.init();
  }

  init() {
    // Listen for quantity changes on main products
    document.addEventListener('change', (e) => {
      if (e.target.classList.contains('quantity__input')) {
        this.handleQuantityChange(e);
      }
    });
  }

  handleQuantityChange(event) {
    const input = event.target;
    const cartItem = input.closest('.cart-item');
    
    if (cartItem && cartItem.classList.contains('cart-item--main')) {
      const bundleId = cartItem.dataset.bundleId;
      const newQuantity = parseInt(input.value);
      
      if (bundleId) {
        this.updateBundleQuantities(bundleId, newQuantity);
      }
    }
  }

  async updateBundleQuantities(bundleId, newQuantity) {
    // Find all addon items for this bundle
    const addonItems = document.querySelectorAll(`tr[data-bundle-id="${bundleId}"].cart-item--addon`);
    
    // Update each addon quantity to match the main product
    for (const addonItem of addonItems) {
      const variantId = addonItem.dataset.variantId;
      if (variantId) {
        try {
          await this.updateCartItemQuantity(variantId, newQuantity);
        } catch (error) {
          console.error(`Failed to update addon quantity for ${variantId}:`, error);
        }
      }
    }
  }

  async updateCartItemQuantity(variantId, quantity) {
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        updates: {
          [variantId]: quantity
        }
      })
    };

    const response = await fetch('/cart/update.js', config);
    if (!response.ok) {
      throw new Error(`Failed to update quantity for variant ${variantId}`);
    }
    
    return response.json();
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.cartItemBundle = new CartItemBundle();
  window.cartBundleQuantityManager = new CartBundleQuantityManager();
});

// Also initialize if script loads after DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartItemBundle = new CartItemBundle();
    window.cartBundleQuantityManager = new CartBundleQuantityManager();
  });
} else {
  window.cartItemBundle = new CartItemBundle();
  window.cartBundleQuantityManager = new CartBundleQuantityManager();
}